import { NgStyle, NgTemplateOutlet, AsyncPipe } from "@angular/common";
import {
    AfterContentInit,
    booleanAttribute,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    TemplateRef,
    ViewChildren,
} from "@angular/core";
import { Subscription } from "rxjs";

import { LayoutFacade } from "@hermes/aphrodite/layout";

import { TabItemComponent } from "../tab-item/tab-item.component";

import { tabListAnimations } from "./tab-list.animation";

export enum KeyboardCodeValue {
    RIGHT = "ArrowRight",
    LEFT = "ArrowLeft",
}

export interface TabChangeEvent {
    index: number;
    tab: TabItemComponent;
}

export const TAB_LIST = "list";
export const TAB_BLOCK = "block";
export type TabAppearance = typeof TAB_LIST | typeof TAB_BLOCK;

export const TAB_PRIMARY = "primary";
export const TAB_SECONDARY = "secondary";
export const TAB_TRANSPARENT = "transparent";
export type TabAppeal =
    | typeof TAB_PRIMARY
    | typeof TAB_SECONDARY
    | typeof TAB_TRANSPARENT;

export const TAB_SEPARATOR_FULL_HEIGHT = "100%";
export const TAB_SEPARATOR_STANDARD_HEIGHT = "60%";
export type TabSeparatorHeight =
    | typeof TAB_SEPARATOR_FULL_HEIGHT
    | typeof TAB_SEPARATOR_STANDARD_HEIGHT;

/**
 * This component implements a Tablist Component
 *
 * It manages accessibility attributes for you, according Tabs design pattern (@see https://www.w3.org/TR/wai-aria-practices/#tabpanel)
 *
 * And takes its inputs from its parents.
 *
 * When using the hTabTitle directive, you can retrieve context information (see https://angular.io/api/common/NgTemplateOutlet).
 *
 * Usage:
 * ```
 * <h-tab-list defaultSelectedTab="1" ariaLabel="name-of-tablist" backgroundColor="red"
 *        (selectedChange)="onTabChanged($event)">
 *
 *  <h-tab-item tabTitle="My Title" i18n-tabTitle="@@hermes.checkout.returning.customer" tabId="my-tab-id" [active]='true'>
 *      <p>Tab Content</p>
 *  </h-tab-item>
 *
 *  <h-tab-item tabId="my-second-tab-id" [active]='true'>
 *    <ng-template [hTabTitle]>
 *      <p>Custom Title Content</p>
 *    </ng-template>
 *    <p>Tab Content</p>
 *  </h-tab-item>
 *
 * </h-tab-list>
 * ```
 *
 */
@Component({
    selector: "h-tab-list",
    templateUrl: "./tab-list.component.html",
    styleUrls: ["./tab-list.component.scss"],
    animations: [tabListAnimations],
    imports: [AsyncPipe, NgStyle, NgTemplateOutlet],
    standalone: true,
})
export class TabListComponent implements OnInit, AfterContentInit, OnDestroy {
    /**
     * Optional input to set the default selected tab (0 is the first one, etc)
     */
    @Input()
    public defaultSelectedTab: number | undefined;

    /**
     * Required Input that provides a label that describes the purpose of the set of tabs.
     */
    @Input()
    public ariaLabel: string | undefined;

    @Input()
    public scrollTopOnTabChange = true;

    /**
     * Appearance of the tab group.
     */
    @Input()
    public appearance: TabAppearance = TAB_LIST;

    @Input()
    public appeal: TabAppeal = TAB_PRIMARY;

    /**
     * Separator height as a percentage of the block height.
     */
    @Input()
    public separatorHeight: TabSeparatorHeight = TAB_SEPARATOR_FULL_HEIGHT;

    @Input({ transform: booleanAttribute })
    public shouldDisplayMarginBottom = true;

    @Input({ transform: booleanAttribute })
    public shouldDisplayVerticalScrollbar = false;

    /**
     * EventEmitter when the user selects a new tab
     */
    @Output()
    public selectedTabChange = new EventEmitter<TabChangeEvent>();

    /**
     * List of child TabItemComponent
     */
    @ContentChildren(TabItemComponent)
    public tabs: QueryList<TabItemComponent> =
        new QueryList<TabItemComponent>();

    @ViewChildren("tabTitle")
    public tabsTitle!: QueryList<ElementRef>;

    /**
     * Value of the current tab selection
     */
    public currentSelectedTab!: number | undefined;

    public changeListAnimation: boolean = false;

    public isKeyboardNavigation$ = this.layoutFacade.keyboardNavigation$;

    private subscription: Subscription = new Subscription();

    constructor(private layoutFacade: LayoutFacade) {}

    public ngOnInit(): void {
        this.currentSelectedTab = this.defaultSelectedTab;

        this.subscription.add(
            this.selectedTabChange.subscribe((event: TabChangeEvent) => {
                this.tabs.forEach((tab) => (tab.display = false));
                event.tab.display = true;
                if (event.index !== this.currentSelectedTab) {
                    this.currentSelectedTab = event.index;
                    // Using setTimeout to ensure the DOM is updated before setting focus.
                    setTimeout(() => {
                        const tabElement =
                            this.tabsTitle.toArray()[event.index]
                                ?.nativeElement;
                        if (tabElement) {
                            tabElement.focus();
                        }
                    });
                }
            }),
        );
    }

    public ngAfterContentInit(): void {
        if (this.currentSelectedTab !== undefined) {
            this.emitNewTab(this.tabs.toArray()[this.currentSelectedTab]);
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Increases (or decreases) the position of the selected tab.
     *
     * @param value positive or negative value
     */
    public incrementCurrentTab(value: number): void {
        const tabsArray = this.tabs.toArray();
        // Get the desired position
        let newIndex = (this.currentSelectedTab ?? 0) + value;
        if (newIndex >= tabsArray.length) {
            newIndex = 0;
        } else if (newIndex < 0) {
            newIndex = tabsArray.length - 1;
        }

        const newTab = this.tabs.find((tab) => tab === tabsArray[newIndex]);

        // If movement is possible, we emit a new position
        // Movement possible => tab exist AND tab is enabled
        if (newTab && newTab.active) {
            this.emitNewTab(newTab);
        } else if (newTab && !newTab.active) {
            // If the new tab exists but is not enable, we ask for a double mouvement
            this.incrementCurrentTab(value * 2);
        }
    }

    /**
     * Event raised when clicking on a `tab`
     *
     * @param tab The `tab` component
     */
    public onClickTab(tab: TabItemComponent): void {
        if (tab.active) {
            this.changeListAnimation =
                this.currentSelectedTab !== this.getTabIndex(tab);
            this.emitNewTab(tab);
        }
    }

    /**
     * Event raised when user use his keyboard on a `tab`
     *
     * @param event the Keyboard event
     */
    public onKeyDown(event: KeyboardEvent): void {
        switch (event.code) {
            case KeyboardCodeValue.LEFT: {
                this.incrementCurrentTab(-1);
                break;
            }
            case KeyboardCodeValue.RIGHT: {
                this.incrementCurrentTab(1);
                break;
            }
            default: {
                // Do nothing
                break;
            }
        }
    }
    /**
     * Format the name of aria-controls attributs.
     *
     * @param tab The `tab` component
     */
    public getAriaControls(tab: TabItemComponent): string {
        return `${tab.tabId}-tab`;
    }

    /**
     * Return the custom title template for a specific tab
     *
     * @param tab The `tab` component
     */
    public getTitleTemplate(
        tab: TabItemComponent,
    ): TemplateRef<TabItemComponent> {
        return tab.customTabTitle.first.templateReference;
    }

    /**
     * Return the custom title template for a specific tab
     *
     * @param tab The `tab` component
     */
    public isFocusable(tabDisplay: boolean, tabIndex: number): number {
        if (this.currentSelectedTab) {
            return tabDisplay ? 0 : -1;
        }
        return tabIndex === 0 ? 0 : -1;
    }

    public onAnimationEnd() {
        this.changeListAnimation = false;
    }

    /**
     * Helpful function to cast then emit a new TabChangeEvent value
     *
     * @param tab The new `tab` component
     */
    private emitNewTab(tab: TabItemComponent) {
        this.selectedTabChange.emit({
            index: this.getTabIndex(tab),
            tab,
        });
    }

    /**
     * Helpful function to get the index of the tab in the tabs variable.
     *
     * @param tab The new `tab` component
     */
    private getTabIndex(tab: TabItemComponent): number {
        return this.tabs.toArray().indexOf(tab);
    }
}
